import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Store, Building2, Home, Users, FileText, CheckSquare, BarChart3, Workflow, Settings, LogOut, Menu, X, FlaskConical, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import AccountDialog from "@/components/layout/AccountDialog";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perms: ["dashboard.view"] },
  { to: "/funnels/dealer", label: "Dealer Funnel", icon: Store, perms: ["leads.view_all", "leads.view_own"] },
  { to: "/funnels/commercial", label: "Commercial Funnel", icon: Building2, perms: ["leads.view_all", "leads.view_own"] },
  { to: "/funnels/residential", label: "Residential Funnel", icon: Home, perms: ["leads.view_all", "leads.view_own"] },
  { to: "/leads", label: "Leads & Customers", icon: Users, perms: ["leads.view_all", "leads.view_own"] },
  { to: "/quotations", label: "Quotations", icon: FileText, perms: ["quotations.view"] },
  { to: "/tasks", label: "Tasks & Follow-ups", icon: CheckSquare, perms: ["leads.view_all", "leads.view_own"] },
  { to: "/reports", label: "Reports", icon: BarChart3, perms: ["reports.view"] },
  { to: "/funnel-builder", label: "Funnel Builder", icon: Workflow, perms: ["workflows.edit"], admin: true },
  { to: "/settings", label: "Users & Settings", icon: Settings, perms: ["users.manage", "settings.manage", "sources.manage", "audit.view"], admin: true },
];

function NavBody({ onNavigate }) {
  const { can, user, logout } = useAuth();
  const [acct, setAcct] = useState(false);
  const items = NAV.filter((n) => can(...n.perms));
  return (
    <div className="flex h-full flex-col bg-[#0B192C] text-slate-300">
      <div className="px-6 pt-7 pb-6 border-b border-white/5">
        <div className="font-display text-xl font-extrabold tracking-tight text-white leading-none">SERATO <span className="text-orange-500">SALESCORE</span></div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-2">Powered by MSC Intelligence</div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-0.5">
        {items.map((n, i) => (
          <div key={n.to}>
            {n.admin && !items[i - 1]?.admin && <div className="px-3 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">Administration</div>}
            <NavLink
              to={n.to}
              onClick={onNavigate}
              data-testid={`nav-${n.to.replace(/\//g, "-").slice(1)}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "bg-white/10 text-white" : "hover:bg-white/5 hover:text-white"}`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`h-4 w-0.5 rounded ${isActive ? "bg-orange-500" : "bg-transparent"}`} />
                  <n.icon className="h-4 w-4 shrink-0" />
                  {n.label}
                </>
              )}
            </NavLink>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/5 p-4">
        <div className="text-sm text-white font-medium truncate" data-testid="current-user-name">{user.name}</div>
        <div className="text-xs text-slate-500 truncate" data-testid="current-user-role">{user.role?.name}</div>
        <div className="mt-3 flex gap-2">
          <button onClick={() => setAcct(true)} className="flex-1 flex items-center justify-center gap-1.5 text-xs rounded border border-white/10 py-1.5 hover:bg-white/5" data-testid="account-button"><KeyRound className="h-3.5 w-3.5" />Account</button>
          <button onClick={logout} className="flex-1 flex items-center justify-center gap-1.5 text-xs rounded border border-white/10 py-1.5 hover:bg-white/5" data-testid="logout-button"><LogOut className="h-3.5 w-3.5" />Sign out</button>
        </div>
      </div>
      <AccountDialog open={acct} onOpenChange={setAcct} />
    </div>
  );
}

export default function AppShell({ children }) {
  const { meta } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const demo = meta?.demo?.loaded;
  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30"><NavBody /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 border-0 [&>button]:hidden"><NavBody onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between bg-[#0B192C] px-4 h-14">
        <button onClick={() => setOpen(true)} className="text-white" data-testid="mobile-menu-button">{open ? <X /> : <Menu />}</button>
        <div className="font-display font-extrabold text-white text-sm tracking-tight">SERATO <span className="text-orange-500">SALESCORE</span></div>
        <span className="w-6" />
      </header>
      {demo && (
        <div className="bg-orange-500 text-white text-xs sm:text-sm px-4 sm:px-8 py-2 flex items-center gap-2" data-testid="demo-mode-banner">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span><b>DEMO MODE</b> — {meta.demo.lead_count} synthetic demonstration records are loaded. They are not Serato's actual business records and can be removed in Users & Settings.</span>
        </div>
      )}
      <main key={loc.pathname} className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1600px] fade-up">{children}</main>
    </div>
  );
}
